import { useState, useMemo, useEffect, useRef } from 'react'
import { Plus, Trash2, Network, Sparkles, Wand2, AlertCircle, ChevronRight, ChevronDown, Pencil } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import type { StructureData, Ou } from '../types.ts'
import { Badge, Button, Card } from '@/components/ui'
import { cn } from '@/lib/utils.ts'

interface Props {
  data: StructureData
  update: (patch: Partial<StructureData>) => void
}

interface StandardItem {
  id: string
  type: string
  name_template: string
  description?: string
  config?: { parent?: string } | null
  sort_order: number
}

const FIXED_ROOT_CHILDREN: ReadonlyArray<{ name: string; description: string }> = [
  { name: 'AB', description: 'Abteilungen' },
  { name: 'LW', description: 'Laufwerke' },
  { name: 'SW', description: 'Softwarezugriffe' },
  { name: 'SA', description: '' },
  { name: 'FU', description: 'Funktionen' },
]

const SYSTEMHAUS_OU_NAME = '_IMP'
const SYSTEMHAUS_OU_DESC = 'Systemhaus-Verwaltung'
const DOMAIN_ROOT_ID = '__domain_root__'

export function OusTab({ data, update }: Props) {
  const prefix = (data.domainPrefix ?? '').trim()

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [addingUnder, setAddingUnder] = useState<string | null>(null)
  const [addingValue, setAddingValue] = useState('')
  const [contextMenu, setContextMenu] = useState<{ id: string; x: number; y: number } | null>(null)
  const contextMenuRef = useRef<HTMLDivElement | null>(null)

  const { data: itemsData, isLoading: standardsLoading } = useQuery<{ items: StandardItem[] }>({
    queryKey: ['it-standard-items'],
    queryFn: () => apiClient.get('/it-structures/global/items').then((r) => r.data),
  })

  const standardOus = useMemo(
    () => (itemsData?.items ?? []).filter((i) => i.type === 'ou'),
    [itemsData],
  )

  const prefixOu = useMemo(
    () =>
      prefix
        ? data.ous.find(
            (o) => o.parent === null && o.name.trim().toLowerCase() === prefix.toLowerCase(),
          ) ?? null
        : null,
    [data.ous, prefix],
  )

  const existingNames = useMemo(
    () => new Set(data.ous.map((o) => o.name.trim().toLowerCase())),
    [data.ous],
  )

  const pendingSuggestions = useMemo(
    () => standardOus.filter((s) => !existingNames.has(s.name_template.trim().toLowerCase())),
    [standardOus, existingNames],
  )

  const missingFixedChildren = useMemo(
    () => FIXED_ROOT_CHILDREN.filter((f) => !existingNames.has(f.name.toLowerCase())),
    [existingNames],
  )

  useEffect(() => {
    if (!contextMenu) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node | null
      if (target && contextMenuRef.current?.contains(target)) return
      setContextMenu(null)
    }
    window.addEventListener('mousedown', handler)
    window.addEventListener('contextmenu', handler)
    return () => {
      window.removeEventListener('mousedown', handler)
      window.removeEventListener('contextmenu', handler)
    }
  }, [contextMenu])

  function updateOu(id: string, patch: Partial<Ou>) {
    update({ ous: data.ous.map((o) => (o.id === id ? { ...o, ...patch } : o)) })
  }

  function deleteOu(id: string) {
    const toDelete = new Set<string>()
    const collect = (parentId: string) => {
      toDelete.add(parentId)
      data.ous.filter((o) => o.parent === parentId).forEach((c) => collect(c.id))
    }
    collect(id)
    update({ ous: data.ous.filter((o) => !toDelete.has(o.id)) })
  }

  function isProtectedOu(ou: Ou): boolean {
    const nameLower = ou.name.trim().toLowerCase()
    if (prefix && ou.parent === null && nameLower === prefix.toLowerCase()) return true
    if (ou.parent === null && nameLower === SYSTEMHAUS_OU_NAME.toLowerCase()) return true
    if (
      prefixOu &&
      ou.parent === prefixOu.id &&
      FIXED_ROOT_CHILDREN.some((f) => f.name.toLowerCase() === nameLower)
    )
      return true
    return false
  }

  function ensurePrefixRoot(additions: Ou[], nameToId: Map<string, string>): string | null {
    if (!prefix) return null
    const key = prefix.toLowerCase()
    let rootId = nameToId.get(key) ?? null
    if (!rootId) {
      const rootOu: Ou = {
        id: crypto.randomUUID(),
        name: prefix,
        parent: null,
        description: 'Kunden-Root-OU',
      }
      additions.push(rootOu)
      nameToId.set(key, rootOu.id)
      rootId = rootOu.id
    }

    for (const fixed of FIXED_ROOT_CHILDREN) {
      const fixedKey = fixed.name.toLowerCase()
      if (nameToId.has(fixedKey)) continue
      const fixedOu: Ou = {
        id: crypto.randomUUID(),
        name: fixed.name,
        parent: rootId,
        ...(fixed.description ? { description: fixed.description } : {}),
      }
      additions.push(fixedOu)
      nameToId.set(fixedKey, fixedOu.id)
    }

    const impKey = SYSTEMHAUS_OU_NAME.toLowerCase()
    if (!nameToId.has(impKey)) {
      const impOu: Ou = {
        id: crypto.randomUUID(),
        name: SYSTEMHAUS_OU_NAME,
        parent: null,
        description: SYSTEMHAUS_OU_DESC,
      }
      additions.push(impOu)
      nameToId.set(impKey, impOu.id)
    }

    return rootId
  }

  function adoptSuggestion(suggestion: StandardItem) {
    if (!prefix) return
    const name = suggestion.name_template.trim()
    if (!name || existingNames.has(name.toLowerCase())) return

    const additions: Ou[] = []
    const nameToId = new Map<string, string>(
      data.ous.map((o) => [o.name.trim().toLowerCase(), o.id]),
    )

    const rootId = ensurePrefixRoot(additions, nameToId)

    let parentId: string | null = rootId
    const parentName = suggestion.config?.parent?.trim()
    if (parentName) {
      const parentKey = parentName.toLowerCase()
      const existingParentId = nameToId.get(parentKey)
      if (existingParentId) {
        parentId = existingParentId
      } else {
        const parentOu: Ou = {
          id: crypto.randomUUID(),
          name: parentName,
          parent: rootId,
        }
        additions.push(parentOu)
        nameToId.set(parentKey, parentOu.id)
        parentId = parentOu.id
      }
    }

    additions.push({
      id: crypto.randomUUID(),
      name,
      parent: parentId,
      ...(suggestion.description ? { description: suggestion.description } : {}),
    })

    update({ ous: [...data.ous, ...additions] })
  }

  function adoptAllSuggestions() {
    if (!prefix) return
    if (pendingSuggestions.length === 0 && prefixOu) return

    const additions: Ou[] = []
    const nameToId = new Map<string, string>(
      data.ous.map((o) => [o.name.trim().toLowerCase(), o.id]),
    )

    const rootId = ensurePrefixRoot(additions, nameToId)

    const withoutParent = pendingSuggestions.filter((s) => !s.config?.parent)
    const withParent = pendingSuggestions.filter((s) => !!s.config?.parent)

    for (const s of withoutParent) {
      const key = s.name_template.trim().toLowerCase()
      if (nameToId.has(key)) continue
      const ou: Ou = {
        id: crypto.randomUUID(),
        name: s.name_template.trim(),
        parent: rootId,
        ...(s.description ? { description: s.description } : {}),
      }
      additions.push(ou)
      nameToId.set(key, ou.id)
    }

    for (const s of withParent) {
      const key = s.name_template.trim().toLowerCase()
      if (nameToId.has(key)) continue
      const parentName = s.config!.parent!.trim()
      const parentKey = parentName.toLowerCase()
      let parentId = nameToId.get(parentKey) ?? null

      if (!parentId) {
        const parentOu: Ou = {
          id: crypto.randomUUID(),
          name: parentName,
          parent: rootId,
        }
        additions.push(parentOu)
        nameToId.set(parentKey, parentOu.id)
        parentId = parentOu.id
      }

      const ou: Ou = {
        id: crypto.randomUUID(),
        name: s.name_template.trim(),
        parent: parentId,
        ...(s.description ? { description: s.description } : {}),
      }
      additions.push(ou)
      nameToId.set(key, ou.id)
    }

    if (additions.length > 0) {
      update({ ous: [...data.ous, ...additions] })
    }
  }

  function createPrefixRootOnly() {
    if (!prefix) return
    const additions: Ou[] = []
    const nameToId = new Map<string, string>(
      data.ous.map((o) => [o.name.trim().toLowerCase(), o.id]),
    )
    ensurePrefixRoot(additions, nameToId)
    if (additions.length > 0) {
      update({ ous: [...data.ous, ...additions] })
    }
  }

  function toggleCollapse(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function expandNode(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  function startAdd(parentId: string | null) {
    const key = parentId ?? DOMAIN_ROOT_ID
    expandNode(parentId ?? DOMAIN_ROOT_ID)
    setAddingUnder(key)
    setAddingValue('')
    setContextMenu(null)
  }

  function commitAdd(parentId: string | null) {
    if (!addingValue.trim()) {
      setAddingUnder(null)
      return
    }
    const ou: Ou = {
      id: crypto.randomUUID(),
      name: addingValue.trim(),
      parent: parentId,
    }
    update({ ous: [...data.ous, ou] })
    setAddingUnder(null)
    setAddingValue('')
  }

  function startEdit(ou: Ou) {
    setEditingId(ou.id)
    setEditValue(ou.name)
    setContextMenu(null)
  }

  function commitEdit() {
    if (editingId && editValue.trim()) {
      updateOu(editingId, { name: editValue.trim() })
    }
    setEditingId(null)
  }

  function handleContextMenu(e: React.MouseEvent, nodeId: string) {
    e.preventDefault()
    e.stopPropagation()
    setContextMenu({ id: nodeId, x: e.clientX, y: e.clientY })
  }

  function renderInlineAdd(parentId: string | null, depth: number) {
    const key = parentId ?? DOMAIN_ROOT_ID
    if (addingUnder !== key) return null
    return (
      <div
        className="flex items-center gap-1 py-1.5 border-b border-gray-100 bg-brand-50"
        style={{ paddingLeft: `${8 + depth * 20}px` }}
      >
        <span className="w-4 h-4 flex-shrink-0" />
        <Network className="w-4 h-4 flex-shrink-0 text-gray-400" />
        <input
          autoFocus
          type="text"
          value={addingValue}
          onChange={(e) => setAddingValue(e.target.value)}
          onBlur={() => commitAdd(parentId)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitAdd(parentId)
            if (e.key === 'Escape') setAddingUnder(null)
          }}
          placeholder="OU-Name …"
          className="flex-1 px-1.5 py-0.5 text-body border border-brand-500 rounded focus:outline-none focus:ring-1 focus:ring-brand-500 bg-white"
        />
        <kbd className="text-caption text-gray-400 pr-1">Enter ↵</kbd>
      </div>
    )
  }

  function renderNode(ou: Ou, depth: number): React.ReactNode {
    const children = data.ous.filter((o) => o.parent === ou.id)
    const hasChildren = children.length > 0 || addingUnder === ou.id
    const isCollapsedNode = collapsed.has(ou.id)

    const isPrefixRoot =
      !!prefix && ou.parent === null && ou.name.trim().toLowerCase() === prefix.toLowerCase()
    const isSystemhaus =
      ou.parent === null &&
      ou.name.trim().toLowerCase() === SYSTEMHAUS_OU_NAME.toLowerCase()
    const isFixedChild =
      !!prefixOu &&
      ou.parent === prefixOu.id &&
      FIXED_ROOT_CHILDREN.some((f) => f.name.toLowerCase() === ou.name.trim().toLowerCase())
    const fixedDesc = isFixedChild
      ? FIXED_ROOT_CHILDREN.find((f) => f.name.toLowerCase() === ou.name.trim().toLowerCase())
          ?.description
      : undefined
    const isEditing = editingId === ou.id
    const protected_ = isProtectedOu(ou)

    return (
      <div key={ou.id}>
        <div
          className="flex items-center gap-1 py-1.5 hover:bg-gray-50 border-b border-gray-100 group cursor-default select-none"
          style={{ paddingLeft: `${8 + depth * 20}px` }}
          onContextMenu={(e) => handleContextMenu(e, ou.id)}
          onDoubleClick={() => !protected_ && startEdit(ou)}
        >
          <button
            className="flex-shrink-0 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded"
            onClick={(e) => {
              e.stopPropagation()
              if (hasChildren) toggleCollapse(ou.id)
            }}
            tabIndex={-1}
          >
            {hasChildren ? (
              isCollapsedNode ? (
                <ChevronRight className="w-3.5 h-3.5" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5" />
              )
            ) : (
              <span className="w-3.5 h-3.5 inline-block" />
            )}
          </button>

          <Network
            className={cn(
              'w-4 h-4 flex-shrink-0',
              isPrefixRoot && 'text-brand-500',
              isSystemhaus && 'text-green-500',
              isFixedChild && 'text-indigo-500',
              !isPrefixRoot && !isSystemhaus && !isFixedChild && 'text-gray-400',
            )}
          />

          {isEditing ? (
            <input
              autoFocus
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitEdit()
                if (e.key === 'Escape') setEditingId(null)
              }}
              className="flex-1 px-1.5 py-0.5 text-body border border-brand-500 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span className="flex-1 text-body text-gray-800 px-1 truncate">{ou.name}</span>
          )}

          {isFixedChild && fixedDesc && (
            <span className="text-caption text-gray-400 flex-shrink-0 hidden md:inline">
              {fixedDesc}
            </span>
          )}
          {isPrefixRoot && (
            <Badge variant="brand" size="sm">Kunden-Root</Badge>
          )}
          {isSystemhaus && (
            <Badge variant="success" size="sm">Systemhaus</Badge>
          )}
          {isFixedChild && (
            <Badge variant="info" size="sm">Pflicht</Badge>
          )}
        </div>

        {!isCollapsedNode && (
          <div>
            {children.map((child) => renderNode(child, depth + 1))}
            {renderInlineAdd(ou.id, depth + 1)}
          </div>
        )}
      </div>
    )
  }

  function renderDomainRoot() {
    const topLevel = data.ous.filter((o) => o.parent === null)

    return (
      <Card>
        {topLevel.map((ou) => renderNode(ou, 0))}
        {renderInlineAdd(null, 0)}
      </Card>
    )
  }

  function renderContextMenu() {
    if (!contextMenu) return null
    const isDomainRoot = contextMenu.id === DOMAIN_ROOT_ID
    const ou =
      !isDomainRoot ? data.ous.find((o) => o.id === contextMenu.id) ?? null : null
    const protected_ = ou ? isProtectedOu(ou) : false

    const menuW = 170
    const menuH = isDomainRoot ? 48 : 130
    const safeX = Math.min(contextMenu.x, window.innerWidth - menuW - 8)
    const safeY = Math.min(contextMenu.y, window.innerHeight - menuH - 8)

    return (
      <div
        ref={contextMenuRef}
        className="fixed z-popover bg-white border border-gray-200 rounded-lg shadow-popover py-1"
        style={{ left: safeX, top: safeY, minWidth: menuW }}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
      >
        <button
          className="w-full text-left px-3 py-1.5 text-body text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          onClick={() => startAdd(isDomainRoot ? null : contextMenu.id)}
        >
          <Plus className="w-3.5 h-3.5" />
          OU hinzufügen
        </button>

        {!isDomainRoot && (
          <>
            <button
              disabled={protected_}
              title={protected_ ? 'Pflicht-OU kann nicht umbenannt werden' : undefined}
              className={cn(
                'w-full text-left px-3 py-1.5 text-body flex items-center gap-2',
                protected_
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-50',
              )}
              onClick={() => ou && !protected_ && startEdit(ou)}
            >
              <Pencil className="w-3.5 h-3.5" />
              Umbenennen
            </button>
            <div className="border-t border-gray-100 my-1" />
            <button
              disabled={protected_}
              title={protected_ ? 'Pflicht-OU kann nicht gelöscht werden' : undefined}
              className={cn(
                'w-full text-left px-3 py-1.5 text-body flex items-center gap-2',
                protected_ ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:bg-red-50',
              )}
              onClick={() => {
                if (ou && !protected_) {
                  deleteOu(ou.id)
                  setContextMenu(null)
                }
              }}
            >
              <Trash2 className="w-3.5 h-3.5" />
              Löschen
            </button>
          </>
        )}
      </div>
    )
  }

  const missingPrefix = !prefix

  return (
    <div className="space-y-4">
      {missingPrefix && (
        <Card className="border-red-200 bg-red-50">
          <div className="px-4 py-3 text-caption text-red-800 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <span className="font-medium">AD-Prefix fehlt.</span> Bitte oben im Feld{' '}
              <span className="font-mono">AD-Prefix</span> das Kundenkürzel eintragen (z.B.{' '}
              <span className="font-mono">KDK</span>). Die Kunden-Root-OU mit den Pflicht-OUs{' '}
              <span className="font-mono">
                {FIXED_ROOT_CHILDREN.map((f) => f.name).join(' / ')}
              </span>{' '}
              und die Standards werden automatisch darunter angelegt.
            </div>
          </div>
        </Card>
      )}

      {!missingPrefix && !prefixOu && (
        <Card className="border-brand-200 bg-brand-50">
          <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <Network className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-body font-semibold text-gray-900">
                  Kunden-Root-OU <span className="font-mono text-brand-700">{prefix}</span> fehlt
                </h3>
                <p className="text-caption text-gray-600 mt-0.5">
                  Jeder Kunde benötigt eine Root-OU mit dem Kundenkürzel, darunter die Pflicht-OUs{' '}
                  <span className="font-mono">
                    {FIXED_ROOT_CHILDREN.map((f) => f.name).join(', ')}
                  </span>
                  . Standard-OUs werden ebenfalls automatisch darunter verankert.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="secondary"
                size="sm"
                leadingIcon={<Plus className="w-3.5 h-3.5" />}
                onClick={createPrefixRootOnly}
              >
                Root + Pflicht-OUs anlegen
              </Button>
              {standardOus.length > 0 && (
                <Button
                  variant="primary"
                  size="sm"
                  leadingIcon={<Wand2 className="w-3.5 h-3.5" />}
                  onClick={adoptAllSuggestions}
                >
                  Root + Pflicht-OUs + {pendingSuggestions.length} Standards anlegen
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {!missingPrefix && prefixOu && missingFixedChildren.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-body font-semibold text-gray-900">
                  Pflicht-OUs fehlen unter{' '}
                  <span className="font-mono text-brand-700">{prefix}</span>
                </h3>
                <p className="text-caption text-gray-600 mt-0.5">
                  Folgende Pflicht-OUs sind noch nicht angelegt:{' '}
                  <span className="font-mono">
                    {missingFixedChildren.map((f) => f.name).join(', ')}
                  </span>
                </p>
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              leadingIcon={<Plus className="w-3.5 h-3.5" />}
              onClick={createPrefixRootOnly}
            >
              Pflicht-OUs nachtragen
            </Button>
          </div>
        </Card>
      )}

      {!standardsLoading &&
        !missingPrefix &&
        prefixOu &&
        standardOus.length > 0 &&
        pendingSuggestions.length > 0 && (
          <Card className="border-brand-100 bg-gradient-to-r from-brand-50 to-green-50">
            <div className="p-4 flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <h3 className="text-body font-semibold text-gray-900">
                      Vorschläge aus den globalen Standards
                    </h3>
                    <p className="text-caption text-gray-600 mt-0.5">
                      Wird unter der Kunden-Root-OU{' '}
                      <span className="font-mono text-brand-700">{prefix}</span> angelegt.
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    leadingIcon={<Wand2 className="w-3.5 h-3.5" />}
                    onClick={adoptAllSuggestions}
                  >
                    Alle {pendingSuggestions.length} übernehmen
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {pendingSuggestions.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => adoptSuggestion(s)}
                      title={
                        (s.config?.parent ? `unter ${s.config.parent}` : `unter ${prefix}`) +
                        (s.description ? ` – ${s.description}` : '')
                      }
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white hover:bg-brand-500 border border-brand-200 hover:border-brand-500 text-gray-700 hover:text-white text-caption font-medium rounded-full shadow-sm transition-colors group"
                    >
                      <Plus className="w-3 h-3 text-brand-500 group-hover:text-white" />
                      {s.name_template}
                      {s.config?.parent && (
                        <span className="text-caption text-gray-400 group-hover:text-brand-100 font-normal">
                          / {s.config.parent}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

      {!standardsLoading && standardOus.length === 0 && data.ous.length === 0 && !missingPrefix && (
        <Card className="border-amber-100 bg-amber-50">
          <div className="px-4 py-3 text-caption text-amber-800">
            <span className="font-medium">Tipp:</span> In den{' '}
            <a href="/infrastruktur/strukturen/global" className="underline hover:text-amber-900">
              globalen Standards
            </a>{' '}
            können Standard-OUs hinterlegt werden, die hier als Vorschlag erscheinen.
          </div>
        </Card>
      )}

      {renderDomainRoot()}

      {renderContextMenu()}
    </div>
  )
}
